import { isPublicApiPath, isPublicAppPath } from '@/utils/public-routes'

const SESSION_REFRESH_API_PATH = '/api/registry/v1/auth/refresh'
// Ein abgelehnter Passkey bei der erneuten Bestätigung antwortet mit 401. Das ist
// ein Eingabefehler auf der Konto-Seite und kein Hinweis auf eine beendete Sitzung.
const SELF_REAUTH_PASSKEY_FINISH_API_PATH = '/api/registry/v1/me/local/reauth/passkey/finish'

// Extrahiert den reinen Pfadanteil (pathname) einer relativen oder absoluten URL.
// Query-String und Fragment werden ignoriert, sodass ein Vergleich nicht auf
// zufällig enthaltene Teilzeichenketten (z. B. in Query-Parametern) hereinfällt.
const pathnameOfUrl = (url: string): string => {
  try {
    // Ein Basis-Origin erlaubt das Parsen relativer wie absoluter URLs.
    return new URL(url, 'http://internal.invalid').pathname
  } catch {
    // Fallback: manuell Query/Fragment abschneiden, falls URL() fehlschlägt.
    return url.split(/[?#]/, 1)[0] ?? url
  }
}

// Der Session-Refresh läuft im Hintergrund (siehe DefaultLayout) und darf bei
// einem 401 NICHT die globale Login-Weiterleitung auslösen. Andernfalls würde
// eine abgelaufene Sitzung den Nutzer aus einer noch offenen Seite herauswerfen,
// obwohl der eigentliche Fehler bereits im Auth-Store still behandelt wird.
// Der Vergleich erfolgt exakt auf dem geparsten Pfad, damit weder Query-Strings
// (z. B. ...?next=/api/registry/v1/auth/refresh) noch Geschwister-Pfade
// (z. B. /api/registry/v1/auth/refresh-other) fälschlich ausgenommen werden.
const isSessionRefreshApiPath = (url?: string) =>
  Boolean(url) && pathnameOfUrl(url!) === SESSION_REFRESH_API_PATH

export interface AuthInterceptorContext {
  status?: number
  url?: string
  pathname?: string
}

// Entscheidet, ob ein fehlgeschlagener Request (401) global zum Login umleiten
// soll. Die Logik ist bewusst als reine Funktion ausgelagert, damit sie ohne
// echten Axios-Interceptor getestet werden kann.
export const shouldRedirectToLoginOnAuthError = ({
  status,
  url,
  pathname = window.location.pathname,
}: AuthInterceptorContext): boolean => {
  if (status !== 401) {
    return false
  }

  // Im Workflow Designer oder wenn spezifische API-Pfade (workflow) fehlschlagen, NICHT zum Login leiten,
  // da der Workflow-Service auch ohne Login funktionieren kann.
  const isWorkflowDesigner = pathname.includes('/workflow-designer')
  const isWorkflowApi = Boolean(url) && url!.includes('/api/workflow/v1/')

  // Öffentliche Routen (z. B. Kunden-Onboarding) haben absichtlich keine Portal-Session.
  const isPublicContext = isPublicAppPath(pathname) || isPublicApiPath(url)

  if (isWorkflowDesigner || isWorkflowApi || isPublicContext) {
    return false
  }

  // Der Hintergrund-Session-Refresh soll nie zum Login leiten.
  if (isSessionRefreshApiPath(url)) {
    return false
  }

  if (url && pathnameOfUrl(url) === SELF_REAUTH_PASSKEY_FINISH_API_PATH) {
    return false
  }

  return true
}

export interface RetriableConfig {
  url?: string
  _authRetried?: boolean
}

export interface AuthErrorHandlerOptions {
  // Führt den Request erneut aus (axios selbst oder eine Instanz davon).
  request: (config: RetriableConfig) => Promise<unknown>
  // Erneuert die Sitzung (POST /auth/refresh); lehnt ab, wenn die Anmeldung beendet ist.
  refresh: () => Promise<unknown>
  redirectToLogin: () => void
}

interface AuthError {
  response?: { status?: number }
  config?: RetriableConfig
}

export const SESSION_REFRESH_URL = SESSION_REFRESH_API_PATH

// Baut den Fehler-Handler des globalen Response-Interceptors: Bei einem 401 wird die
// Sitzung einmalig still erneuert (ein gemeinsamer Refresh für parallele Requests) und der
// ursprüngliche Request genau einmal wiederholt. Erst wenn das scheitert, geht es zum Login.
export const createAuthErrorHandler = ({
  request,
  refresh,
  redirectToLogin,
}: AuthErrorHandlerOptions) => {
  let inFlightRefresh: Promise<unknown> | null = null

  const sharedRefresh = () => {
    if (!inFlightRefresh) {
      inFlightRefresh = refresh().finally(() => {
        inFlightRefresh = null
      })
    }
    return inFlightRefresh
  }

  return async (error: AuthError): Promise<unknown> => {
    const config = error.config
    if (!shouldRedirectToLoginOnAuthError({ status: error.response?.status, url: config?.url })) {
      throw error
    }

    if (!config || config._authRetried) {
      redirectToLogin()
      throw error
    }

    try {
      await sharedRefresh()
    } catch {
      redirectToLogin()
      throw error
    }

    config._authRetried = true
    return request(config)
  }
}
